import React, { FunctionComponent } from 'react';
import { Button, Divider, Grid, Paper } from '@mui/material';

import { MultiValueBooleanInput } from '../../multi-value-controls/MultiValueBooleanInput';
import { MultiValueNumberInput } from '../../multi-value-controls/MultiValueNumberInput';
import { LoadSettingsControlProps } from '../LoadSettingsControls';
import { emphasizedControl } from './emphasizedControl';

export const RoofTCDeadSettings: FunctionComponent<LoadSettingsControlProps> = (props) => (
  <DeadSettings {...props} roofOrFloor="roof" topChordOrBottomChord="topChord" />
);

export const RoofBCDeadSettings: FunctionComponent<LoadSettingsControlProps> = (props) => (
  <DeadSettings {...props} roofOrFloor="roof" topChordOrBottomChord="bottomChord" />
);

export const FloorTCDeadSettings: FunctionComponent<LoadSettingsControlProps> = (props) => (
  <DeadSettings {...props} roofOrFloor="floor" topChordOrBottomChord="topChord" />
);

export const FloorBCDeadSettings: FunctionComponent<LoadSettingsControlProps> = (props) => (
  <DeadSettings {...props} roofOrFloor="floor" topChordOrBottomChord="bottomChord" />
);

interface DeadSettingsOptions extends LoadSettingsControlProps {
  roofOrFloor: 'roof' | 'floor';
  topChordOrBottomChord: 'topChord' | 'bottomChord';
}

/** When no override is set, the max dead load for uplift defaults to 0.6 of the dead load. */
const defaultMaxDeadLoadForUpliftFactor = 0.6;

const DeadSettings: FunctionComponent<DeadSettingsOptions> = ({
  roofOrFloor,
  topChordOrBottomChord,
  disabled,
  loadSettingsArray,
  setLoadSettingsValues,
}) => {
  const deadLoadingKey = getDeadLoadingKey({ roofOrFloor, topChordOrBottomChord });
  const addHalfWeightKey = getAddHalfWeightKey({ roofOrFloor, topChordOrBottomChord });
  const upliftOverrideKey =
    topChordOrBottomChord === 'topChord'
      ? 'maxTopChordDeadLoadForUpliftOverride'
      : 'maxBottomChordDeadLoadForUpliftOverride';
  return (
    <>
      <Grid size={6}>
        <MultiValueNumberInput
          values={loadSettingsArray.map((loadSettings) => loadSettings[deadLoadingKey])}
          submit={(value) => setLoadSettingsValues({ [deadLoadingKey]: value })}
          label={`${topChordOrBottomChord === 'topChord' ? 'TC' : 'BC'} Dead Load`}
          unit="psf"
          disabled={disabled}
        />
      </Grid>
      <Grid size={6}>
        <MultiValueBooleanInput
          values={loadSettingsArray.map((loadSettings) => loadSettings[addHalfWeightKey])}
          onChange={(value) => setLoadSettingsValues({ [addHalfWeightKey]: value })}
          label="Add self-weight of the component"
          disabled={disabled}
        />
      </Grid>
      <Grid size={6}>
        <MultiValueNumberInput
          values={loadSettingsArray.map((loadSettings) =>
            loadSettings[upliftOverrideKey] !== null
              ? loadSettings[upliftOverrideKey]
              : loadSettings[deadLoadingKey] * defaultMaxDeadLoadForUpliftFactor,
          )}
          submit={(value) => setLoadSettingsValues({ [upliftOverrideKey]: value })}
          label={`Max ${topChordOrBottomChord === 'topChord' ? 'TC' : 'BC'} dead load for uplift (${
            loadSettingsArray.some((loadSettings) => loadSettings[upliftOverrideKey] != null) ? 'custom' : 'default'
          })`}
          unit="psf"
          disabled={disabled || loadSettingsArray.every((loadSettings) => loadSettings[upliftOverrideKey] == null)}
        />
      </Grid>
      <Grid size={6}>
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            setLoadSettingsValues({
              [upliftOverrideKey]: loadSettingsArray.every((loadSettings) => loadSettings[upliftOverrideKey] == null)
                ? loadSettingsArray[0][deadLoadingKey] * defaultMaxDeadLoadForUpliftFactor
                : null,
            })
          }
          disabled={disabled}
        >
          {loadSettingsArray.every((loadSettings) => loadSettings[upliftOverrideKey] == null) ? 'Edit' : 'Use Default'}
        </Button>
      </Grid>
      {roofOrFloor === 'roof' && (
        <Grid size={12}>
          <Paper sx={emphasizedControl}>
            <MultiValueNumberInput
              values={loadSettingsArray.map((loadSettings) => loadSettings.overhangDeadLoad)}
              submit={(overhangDeadLoad) => setLoadSettingsValues({ overhangDeadLoad })}
              label="Overhang soffit dead load"
              unit="psf"
              disabled={disabled}
            />
          </Paper>
        </Grid>
      )}
      <Grid size={12}>
        <Divider />
      </Grid>
    </>
  );
};

const getDeadLoadingKey = (deadSettingsOptions: Pick<DeadSettingsOptions, 'roofOrFloor' | 'topChordOrBottomChord'>) => {
  const { roofOrFloor, topChordOrBottomChord } = deadSettingsOptions;
  switch (roofOrFloor) {
    case 'roof': {
      switch (topChordOrBottomChord) {
        case 'topChord':
          return 'roofTopChordDeadLoad';
        case 'bottomChord':
          return 'roofBottomChordDeadLoad';
      }
    }
    case 'floor': {
      switch (topChordOrBottomChord) {
        case 'topChord':
          return 'floorTopChordDeadLoad';
        case 'bottomChord':
          return 'floorBottomChordDeadLoad';
      }
    }
  }
};

const getAddHalfWeightKey = (
  deadSettingsOptions: Pick<DeadSettingsOptions, 'roofOrFloor' | 'topChordOrBottomChord'>,
) => {
  const { roofOrFloor, topChordOrBottomChord } = deadSettingsOptions;
  switch (roofOrFloor) {
    case 'roof': {
      switch (topChordOrBottomChord) {
        case 'topChord':
          return 'addHalfWeightOfTrussToRoofTCDeadLoad';
        case 'bottomChord':
          return 'addHalfWeightOfTrussToRoofBCDeadLoad';
      }
    }
    case 'floor': {
      switch (topChordOrBottomChord) {
        case 'topChord':
          return 'addHalfWeightOfTrussToFloorTCDeadLoad';
        case 'bottomChord':
          return 'addHalfWeightOfTrussToFloorBCDeadLoad';
      }
    }
  }
};
